
const ALGO = "AES-GCM"; // Web Crypto API format
const IV_LENGTH = 12;
const SALT_LENGTH = 16;
const KEY_LENGTH = 32;
const AUTH_TAG_LENGTH = 16;
// const PBKDF2_ITERATIONS = 150000;
const PBKDF2_ITERATIONS = 100_000;
const DIGEST = "sha256";
const ENCRYPTION_SECRET = "dev_dummy_secret_9F3kL#2sP@q8XzA";
const HMAC_SECRET = "hmac_dummy_secret_@9X2";
const REQUEST_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

if (!ENCRYPTION_SECRET || !HMAC_SECRET) {
  throw new Error("Missing encryption env secrets");
}

async function deriveKey(secret, salt) {
  const secretKey = new TextEncoder().encode(secret);
  
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    secretKey,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );
  
  const hashAlgo = DIGEST === 'sha256' ? 'SHA-256' : DIGEST.toUpperCase();
  
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: PBKDF2_ITERATIONS,
      hash: hashAlgo
    },
    keyMaterial,
    {
      name: ALGO,
      length: KEY_LENGTH * 8 // KEY_LENGTH is in bytes, convert to bits
    },
    false,
    ['decrypt']
  );
  
  return key;
}

async function sign(envelope) {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(HMAC_SECRET);
  const messageData = encoder.encode(envelope);
  
  // Map DIGEST to Web Crypto API format
  const hashAlgo = DIGEST === 'sha256' ? 'SHA-256' : DIGEST.toUpperCase();
  
  const key = await crypto.subtle.importKey(
    'raw',
    keyData,
    {
      name: 'HMAC',
      hash: hashAlgo
    },
    false,
    ['sign']
  );
  
  const signature = await crypto.subtle.sign('HMAC', key, messageData);
  
  // Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(signature));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

async function verifySignature(data, signature) {
  const expected = await sign(data);
  
  // Constant-time comparison to prevent timing attacks
  if (expected?.length !== signature?.length) {
    throw new Error("Invalid HMAC signature");
  }
  
  let result = 0;
  for (let i = 0; i < expected?.length; i++) {
    result |= expected?.charCodeAt(i) ^ signature?.charCodeAt(i);
  }
  
  if (result !== 0) {
    throw new Error("Invalid HMAC signature");
  }
}

/* ---------------- ANTI-REPLAY PROTECTION ---------------- */
const usedNonces = new Set();

/**
 * Checks if a nonce has been used before (replay attack detection)
 * @param {string} nonce - The nonce to check
 * @throws {Error} If nonce has been used before
 */
function checkNonce(nonce) {
  if (usedNonces.has(nonce)) {
    throw new Error("Replay attack detected");
  }
  usedNonces.add(nonce);
  
  // Clean up old nonces periodically (keep last 1000)
  if (usedNonces.size > 1000) {
    const noncesArray = Array.from(usedNonces);
    usedNonces.clear();
    // Keep the most recent 500 nonces
    noncesArray.slice(-500).forEach(n => usedNonces.add(n));
  }
}

export async function hybridDecrypt(encrypted) {
  // if (!encrypted?.envelope || !encrypted?.signature) {
  //   throw new Error("Invalid encrypted object");
  // }
  
  try {
    // Verify signature
    await verifySignature(encrypted?.envelope, encrypted?.signature);
    
    // Decode the envelope from base64
    const buffer = Uint8Array.from(atob(encrypted?.envelope), c => c?.charCodeAt(0));
    
    // Extract components from envelope
    const salt = buffer.slice(0, SALT_LENGTH);
    const iv = buffer.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const authTag = buffer.slice(
      SALT_LENGTH + IV_LENGTH,
      SALT_LENGTH + IV_LENGTH + AUTH_TAG_LENGTH
    );
    const ciphertext = buffer.slice(
      SALT_LENGTH + IV_LENGTH + AUTH_TAG_LENGTH
    );
    
    // Derive key from secret and salt
    const key = await deriveKey(ENCRYPTION_SECRET, salt);
    
    // Combine ciphertext and auth tag for decryption
    // In Web Crypto API, the auth tag must be appended to the ciphertext
    const encryptedData = new Uint8Array([...ciphertext, ...authTag]);
    
    // Decrypt using AES-GCM
    // Note: Web Crypto API expects auth tag to be appended to ciphertext
    // The auth tag length is automatically determined (defaults to 128 bits = 16 bytes)
    const decrypted = await crypto.subtle.decrypt(
      {
        name: ALGO,
        iv: iv
      },
      key,
      encryptedData
    );
    
    // Parse decrypted data
    const decryptedText = new TextDecoder().decode(decrypted);
    const parsed = JSON.parse(decryptedText);
    
    // Anti-replay protection: Check nonce
    checkNonce(parsed.nonce);
    
    // Check timestamp expiry (5 minutes)
    if (Date.now() - parsed.timestamp > REQUEST_EXPIRY_MS) {
      throw new Error("Request expired");
    }
    
    return parsed.payload;
  } catch (error) {
    console.error("Error decrypting payload:", error);
    throw error;
  }
}

export async function decryptBackendResponse(encryptedResponse) {
  try {
    if (!encryptedResponse) {
      return encryptedResponse;
    }

    let encryptedData = null;
    let signature = null;

    // Handle different response formats
    if (typeof encryptedResponse === 'string') {
      // Format 1: Direct encrypted string (try to parse as JSON first)
      try {
        const parsed = JSON.parse(encryptedResponse);
        if (parsed.envelope && parsed.signature) {
          encryptedData = parsed;
          signature = parsed.signature;
        } else {
          // Not in expected format, return as is
          return encryptedResponse;
        }
      } catch (e) {
        // Not JSON, return as is
        return encryptedResponse;
      }
    } else if (typeof encryptedResponse === 'object' && encryptedResponse !== null) {
      // Format 2: { envelope: "...", signature: "..." }
      if (encryptedResponse.envelope && encryptedResponse.signature) {
        encryptedData = encryptedResponse;
        signature = encryptedResponse.signature;
      }
      // Format 3: { encrypted: "...", hmac: "..." } or { encrypted: "...", signature: "..." }
      else if (encryptedResponse.encrypted) {
        // Try to parse encrypted field as JSON
        try {
          const parsed = JSON.parse(encryptedResponse.encrypted);
          if (parsed.envelope && parsed.signature) {
            encryptedData = parsed;
            signature = parsed.signature;
          } else {
            return encryptedResponse;
          }
        } catch (e) {
          return encryptedResponse;
        }
      }
      // Format 4: { data: "...", signature: "..." }
      else if (encryptedResponse.data) {
        try {
          const parsed = typeof encryptedResponse.data === 'string' 
            ? JSON.parse(encryptedResponse.data) 
            : encryptedResponse.data;
          if (parsed.envelope && parsed.signature) {
            encryptedData = parsed;
            signature = parsed.signature;
          } else {
            return encryptedResponse;
          }
        } catch (e) {
          return encryptedResponse;
        }
      }
      // Format 5: Response might be nested
      else if (encryptedResponse.response) {
        return await decryptBackendResponse(encryptedResponse.response);
      }
      // If no encrypted field found, return as is
      else {
        return encryptedResponse;
      }
    } else {
      // Not a string or object, return as is
      return encryptedResponse;
    }

    if (!encryptedData || !signature) {
      return encryptedResponse;
    }

    // Decrypt using hybridDecrypt
    try {
      return await hybridDecrypt({
        envelope: encryptedData.envelope || encryptedData,
        signature: signature
      });
    } catch (error) {
      console.error("Decryption failed:", error);
      // Return original response if decryption fails
      return encryptedResponse;
    }
  } catch (error) {
    console.error("Error in decryptBackendResponse:", error);
    // Return original response if decryption fails
    return encryptedResponse;
  }
}

export function isEncryptedResponse(response) {
  if (!response) return false;
  
  if (typeof response === 'string') {
    // Check if it's a base64-like string
    return /^[A-Za-z0-9+/=]+$/.test(response) && response?.length > 20;
  }
  
  if (typeof response === 'object' && response !== null) {
    // Check for encrypted response fields
    return !!(
      response.envelope ||
      (response.encrypted && typeof response.encrypted === 'string') ||
      (response.data && typeof response.data === 'string' && response.data?.length > 20) ||
      response.ciphertext
    );
  }
  
  return false;
}
